import type { ReactNode, Ref } from 'react'
import cx from 'classnames'
import { Link } from 'react-router'
import { Skeleton, useSkeletonListContext } from '@/components/Skeleton'
import styles from './JobSidebarItem.module.css'

export interface JobSidebarItemProps {
  to: string
  isActive: boolean
  jobName: string
  status: ReactNode
  client: string
  isOverdue: boolean
  unreadCount: number
  dueLabel: string
  subtitle: string
}

/**
 * A job in the sidebar reads the way pros refer to it: the client first, then
 * which job and where it stands, with the due date and unread dot on the right.
 */
export function JobSidebarItem({
  to,
  isActive,
  jobName,
  status,
  client,
  isOverdue,
  unreadCount,
  dueLabel,
  subtitle,
}: JobSidebarItemProps) {
  return (
    <Link
      to={to}
      className={cx(styles.root, isActive && styles.active)}
      data-test={`job-list-item-${jobName}`}
    >
      <span className={styles.status}>{status}</span>
      <span className={styles.name}>{client}</span>
      <span className={cx(styles.right, isOverdue && styles.overdue)}>
        {unreadCount > 0 && (
          <span className={styles.dot} title={`${unreadCount} unread`} />
        )}
        {dueLabel}
      </span>
      <span className={styles.sub}>{subtitle}</span>
    </Link>
  )
}

/**
 * A placeholder for a {@link JobSidebarItem} still loading. Render it inside a
 * `SkeletonList` or `SkeletonListInfiniteScrollTrigger`.
 */
export function JobSidebarItemSkeleton() {
  const { varyPercent, ref } = useSkeletonListContext()
  return (
    <div
      ref={ref as Ref<HTMLDivElement> | undefined}
      className={cx(styles.root, styles.skeleton)}
      aria-hidden
    >
      <span className={styles.status}>
        <Skeleton block variant="circle" width={14} height={14} />
      </span>
      <span className={styles.name}>
        <Skeleton width={varyPercent(55)} />
      </span>
      <span className={styles.right}>
        <span>
          <Skeleton width={36} />
        </span>
      </span>
      <span className={styles.sub}>
        <Skeleton width={varyPercent(70)} />
      </span>
    </div>
  )
}
